import React from "react";
import { useTranslation } from "react-i18next";
import {
  Check, Plus, MapPin, Briefcase, CalendarClock, Languages, X, Award, Target,
  Clock, Inbox, Star, SlidersHorizontal, RotateCcw, Coins, Send, Sparkles,
} from "lucide-react";
import { fileUrl } from "@/lib/api";
import { UserAvatar, Stars, VerifiedBadge, Pill, EmptyState, Spinner } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { FIELDS } from "@/lib/constants";
import { openSlots, countryOf, EXP_LEVELS } from "./helpers";

export function MetaItem({ icon: Icon, children }) {
  if (!children) return null;
  return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground min-w-0"><Icon size={12} className="shrink-0" /> <span className="truncate">{children}</span></span>;
}

export function StatPill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground">
      <Icon size={13} className="text-primary" /> {children}
    </span>
  );
}

// Status-aware action button used across all views
export function NetAction({ status, id, onInvite, onRemove, onGoRequests, full = false }) {
  const { t } = useTranslation();
  const cls = `rounded-lg gap-1 active:scale-[0.98] transition-transform ${full ? "flex-1" : ""}`;
  if (status === "in_network")
    return <Button data-testid={`toggle-network-${id}`} size="sm" variant="outline" onClick={() => onRemove(id)} className={cls}><X size={14} /> {t("common.remove")}</Button>;
  if (status === "outgoing")
    return <Button data-testid={`req-pending-${id}`} size="sm" variant="outline" disabled className={`${cls} text-muted-foreground`}><Clock size={14} /> {t("common.invited")}</Button>;
  if (status === "incoming")
    return <Button data-testid={`req-respond-${id}`} size="sm" onClick={onGoRequests} className={cls}><Inbox size={14} /> {t("common.respond")}</Button>;
  return <Button data-testid={`invite-${id}`} size="sm" onClick={() => onInvite(id)} className={cls}><Plus size={14} /> {t("common.invite")}</Button>;
}

export function StatusTag({ status }) {
  const { t } = useTranslation();
  if (status === "in_network") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5"><Check size={10} /> {t("hr.network.inNetworkTag")}</span>;
  if (status === "outgoing") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-warning bg-warning/10 rounded-full px-2 py-0.5"><Clock size={10} /> {t("hr.network.invitedTag")}</span>;
  if (status === "incoming") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-warning bg-warning/10 rounded-full px-2 py-0.5"><Inbox size={10} /> {t("hr.network.wantsToJoin")}</span>;
  return null;
}

/* -------------------- Views -------------------- */
export function RecoCard({ e, reason, action, onView }) {
  const { t } = useTranslation();
  return (
    <div data-testid={`reco-expert-${e.expert_id}`}
      className="min-w-[248px] w-[248px] shrink-0 snap-start rounded-[0.8rem] border border-border bg-card p-4 flex flex-col transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft">
      <button type="button" data-testid={`reco-view-${e.expert_id}`} onClick={() => onView(e)} className="text-left group flex items-center gap-2.5">
        <UserAvatar src={fileUrl(e.picture)} name={e.name} size={40} />
        <div className="min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{e.name}</p>
            {e.verified && <span className="shrink-0"><VerifiedBadge label="" /></span>}
          </div>
          <p className="text-xs text-muted-foreground truncate">{e.title}</p>
        </div>
      </button>
      {reason && (
        <div className="mt-2.5">
          <Pill className="bg-primary/10 text-primary gap-1"><Target size={11} /> {t("hr.network.reco.match", { field: reason })}</Pill>
        </div>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <Stars value={e.rating_avg} count={e.rating_count} size={12} />
        {e.corp_rate_str && (
          <span data-testid={`reco-rate-${e.expert_id}`} className="text-xs font-semibold tabular text-foreground whitespace-nowrap">{e.corp_rate_str}</span>
        )}
      </div>
      <div className="mt-3">{action}</div>
    </div>
  );
}

export function ExpertCard({ e, status, actions, onView, i }) {
  const { t } = useTranslation();
  const slots = openSlots(e);
  const langs = (e.languages || []).slice(0, 2).join(", ") + ((e.languages || []).length > 2 ? ` +${e.languages.length - 2}` : "");
  return (
    <div data-testid={`network-expert-${e.expert_id}`}
      className={`card-milled rounded-[0.8rem] p-5 animate-fade-up d${(i % 6) + 1} transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft flex flex-col ${status === "in_network" ? "ring-1 ring-primary/40" : ""}`}>
      <button type="button" data-testid={`view-expert-${e.expert_id}`} onClick={() => onView(e)} className="text-left group block">
        <div className="flex items-start gap-3">
          <UserAvatar src={fileUrl(e.picture)} name={e.name} size={48} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-1.5 min-w-0">
              <p className="font-medium text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">{e.name}</p>
              {e.verified && <span className="shrink-0 mt-0.5"><VerifiedBadge label="" /></span>}
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{e.title}</p>
            <div className="mt-1.5"><StatusTag status={status} /></div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3 line-clamp-2 leading-relaxed min-h-[2.5em]">{e.headline || e.bio}</p>
      </button>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5">
        <MetaItem icon={MapPin}>{e.location || t("hr.network.remote")}</MetaItem>
        <MetaItem icon={Briefcase}>{e.years_experience ? t("hr.network.yearsShort", { count: e.years_experience }) : null}</MetaItem>
        <MetaItem icon={Languages}>{langs}</MetaItem>
        <MetaItem icon={CalendarClock}>{slots ? t("hr.network.slotsOpen", { count: slots }) : t("hr.network.noSlots")}</MetaItem>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5 min-h-[1.75rem]">
        {(e.fields || []).slice(0, 2).map((f) => <Pill key={f} className="bg-primary/10 text-primary">{f}</Pill>)}
        {(e.fields || []).length > 2 && <Pill className="bg-secondary text-muted-foreground">+{e.fields.length - 2}</Pill>}
      </div>
      <div className="mt-auto pt-4 border-t border-border">
        {e.corp_band && (
          <div className="flex items-center gap-1.5 text-xs mb-3" data-testid={`card-rate-${e.expert_id}`}>
            <Coins size={13} className="text-primary shrink-0" />
            <span className="font-semibold text-foreground tabular">{e.corp_rate_str}</span>
            <span className="text-muted-foreground truncate">{t("rates.perDay")} · {e.corp_band}</span>
          </div>
        )}
        <div className="mb-3"><Stars value={e.rating_avg} count={e.rating_count} size={13} /></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onView(e)} className="flex-1 rounded-lg text-muted-foreground hover:text-foreground" data-testid={`open-view-${e.expert_id}`}>{t("common.view")}</Button>
          {actions}
        </div>
      </div>
    </div>
  );
}

export function ExpertRow({ e, status, actions, onView }) {
  const { t } = useTranslation();
  const slots = openSlots(e);
  return (
    <div data-testid={`network-expert-${e.expert_id}`}
      className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border p-4 transition-colors hover:border-primary/30 hover:bg-secondary/30 ${status === "in_network" ? "border-primary/30" : "border-border"}`}>
      <button type="button" data-testid={`view-expert-${e.expert_id}`} onClick={() => onView(e)} className="flex items-center gap-3 min-w-0 flex-1 text-left group">
        <UserAvatar src={fileUrl(e.picture)} name={e.name} size={44} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{e.name}</p>
            {e.verified && <VerifiedBadge label="" />}
            <StatusTag status={status} />
          </div>
          <p className="text-xs text-muted-foreground truncate">{e.title}</p>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            <MetaItem icon={MapPin}>{e.location || t("hr.network.remote")}</MetaItem>
            <MetaItem icon={Briefcase}>{e.years_experience ? t("hr.network.yearsShort", { count: e.years_experience }) : null}</MetaItem>
            <MetaItem icon={CalendarClock}>{slots ? t("hr.network.slotsOpenShort", { count: slots }) : t("hr.network.noSlotsShort")}</MetaItem>
          </div>
        </div>
      </button>
      <div className="flex items-center gap-3 shrink-0">
        {e.corp_band && (
          <div className="hidden lg:flex items-center gap-1 text-xs" data-testid={`row-rate-${e.expert_id}`}>
            <Coins size={13} className="text-primary" />
            <span className="font-semibold text-foreground tabular">{e.corp_rate_str}</span>
            <span className="text-muted-foreground">{t("rates.perDay")}</span>
          </div>
        )}
        <div className="hidden md:block"><Stars value={e.rating_avg} count={e.rating_count} size={13} /></div>
        <Button variant="outline" size="sm" onClick={() => onView(e)} className="rounded-lg text-muted-foreground hover:text-foreground" data-testid={`open-view-${e.expert_id}`}>{t("common.view")}</Button>
        {actions}
      </div>
    </div>
  );
}

export function ExpertTable({ list, statusOf, renderAction, onView }) {
  const { t } = useTranslation();
  return (
    <div className="card-milled rounded-[0.8rem] overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t("hr.network.tableExpert")}</TableHead>
            <TableHead className="hidden lg:table-cell">{t("hr.network.tableFocus")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("hr.network.tableExp")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("hr.network.tableRate")}</TableHead>
            <TableHead className="hidden lg:table-cell">{t("hr.network.tableCountry")}</TableHead>
            <TableHead className="hidden xl:table-cell">{t("hr.network.tableLanguages")}</TableHead>
            <TableHead>{t("hr.network.tableRating")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("hr.network.tableSlots")}</TableHead>
            <TableHead className="text-right">{t("hr.network.tableAction")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((e) => {
            const status = statusOf(e.expert_id);
            return (
              <TableRow key={e.expert_id} data-testid={`network-expert-${e.expert_id}`}>
                <TableCell>
                  <button type="button" data-testid={`view-expert-${e.expert_id}`} onClick={() => onView(e)} className="flex items-center gap-3 text-left group max-w-[240px]">
                    <UserAvatar src={fileUrl(e.picture)} name={e.name} size={36} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1"><p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{e.name}</p>{e.verified && <VerifiedBadge label="" />}</div>
                      <p className="text-xs text-muted-foreground truncate">{e.title}</p>
                    </div>
                  </button>
                </TableCell>
                <TableCell className="hidden lg:table-cell"><span className="text-xs text-muted-foreground">{(e.fields || []).slice(0, 2).join(", ")}</span></TableCell>
                <TableCell className="hidden md:table-cell tabular text-sm">{e.years_experience ? `${e.years_experience}y` : "—"}</TableCell>
                <TableCell className="hidden md:table-cell">
                  {e.corp_band ? (
                    <div className="tabular text-sm whitespace-nowrap">
                      <span className="font-medium text-foreground">{e.corp_rate_str}</span>
                      <span className="block text-[11px] text-muted-foreground">{e.corp_band}</span>
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{countryOf(e.location)}</TableCell>
                <TableCell className="hidden xl:table-cell text-xs text-muted-foreground">{(e.languages || []).join(", ")}</TableCell>
                <TableCell><Stars value={e.rating_avg} count={e.rating_count} size={12} /></TableCell>
                <TableCell className="hidden md:table-cell tabular text-sm">{openSlots(e)}</TableCell>
                <TableCell className="text-right">{renderAction(e, status)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/* -------------------- Filter panel -------------------- */
export function FilterSheet({ open, onOpenChange, filters, setFilters, countries, languages, activeCount }) {
  const { t } = useTranslation();
  const toggleArr = (key, val) => setFilters((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  const clearAll = () => setFilters({ fields: [], minExp: 0, country: "all", languages: [] });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="filter-sheet">
        <SheetHeader className="p-6 border-b border-border text-left space-y-1">
          <SheetTitle className="flex items-center gap-2"><SlidersHorizontal size={18} className="text-primary" /> {t("hr.network.filterTitle")}</SheetTitle>
          <SheetDescription>{t("hr.network.filterDesc")}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-7">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t("hr.network.focusAreas")}</p>
            <div className="grid grid-cols-1 gap-2.5">
              {FIELDS.map((f) => (
                <label key={f} className="flex items-center gap-2.5 cursor-pointer text-sm text-foreground">
                  <Checkbox data-testid={`filter-field-${f}`} checked={filters.fields.includes(f)} onCheckedChange={() => toggleArr("fields", f)} /> {f}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t("hr.network.experience")}</p>
            <RadioGroup value={String(filters.minExp)} onValueChange={(v) => setFilters((f) => ({ ...f, minExp: Number(v) }))} className="space-y-2.5">
              {EXP_LEVELS.map((val) => (
                <label key={val} className="flex items-center gap-2.5 cursor-pointer text-sm text-foreground">
                  <RadioGroupItem data-testid={`filter-exp-${val}`} value={String(val)} /> {t(`hr.network.expLevels.${val}`)}
                </label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t("hr.network.country")}</p>
            <Select value={filters.country} onValueChange={(v) => setFilters((f) => ({ ...f, country: v }))}>
              <SelectTrigger data-testid="filter-country" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("hr.network.allCountries")}</SelectItem>
                {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-3">{t("hr.network.languages")}</p>
            <div className="grid grid-cols-2 gap-2.5">
              {languages.map((l) => (
                <label key={l} className="flex items-center gap-2.5 cursor-pointer text-sm text-foreground">
                  <Checkbox data-testid={`filter-lang-${l}`} checked={filters.languages.includes(l)} onCheckedChange={() => toggleArr("languages", l)} /> {l}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-border p-4 flex items-center gap-2">
          <Button variant="outline" onClick={clearAll} className="rounded-lg gap-1.5" data-testid="filter-clear"><RotateCcw size={15} /> {t("common.clearAll")}</Button>
          <Button onClick={() => onOpenChange(false)} className="flex-1 rounded-lg" data-testid="filter-apply">{t("common.showResults")}{activeCount ? ` (${activeCount})` : ""}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------- Detail slide-over -------------------- */
export function ExpertSheet({ open, onOpenChange, expert, loading, status, onInvite, onRemove }) {
  const { t } = useTranslation();
  const slots = expert ? openSlots(expert) : 0;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-y-auto" data-testid="expert-net-sheet">
        {!expert ? null : (
          <div className="flex flex-col min-h-full">
            <div className="p-6 border-b border-border bg-gradient-to-b from-secondary/40 to-transparent">
              <SheetHeader className="text-left space-y-0">
                <div className="flex items-start gap-4">
                  <UserAvatar src={fileUrl(expert.picture)} name={expert.name} size={64} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <SheetTitle className="text-xl font-semibold tracking-tight text-foreground truncate">{expert.name}</SheetTitle>
                      {expert.verified && <VerifiedBadge label="" />}
                    </div>
                    <SheetDescription className="text-sm text-muted-foreground">{expert.title}</SheetDescription>
                    <div className="mt-2 flex items-center gap-2"><Stars value={expert.rating_avg} count={expert.rating_count} size={14} /><StatusTag status={status} /></div>
                  </div>
                </div>
              </SheetHeader>
              {expert.headline && <p className="text-sm text-foreground mt-4 leading-relaxed">{expert.headline}</p>}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <MetaItem icon={MapPin}>{expert.location || t("hr.network.remote")}</MetaItem>
                <MetaItem icon={Briefcase}>{expert.years_experience ? t("hr.network.years", { count: expert.years_experience }) : null}</MetaItem>
                <MetaItem icon={Languages}>{(expert.languages || []).join(", ")}</MetaItem>
                <MetaItem icon={CalendarClock}>{slots ? t("hr.network.slotsOpen", { count: slots }) : t("hr.network.noSlots")}</MetaItem>
              </div>
            </div>
            <div className="p-6 space-y-6 flex-1">
              {loading && <div className="grid place-items-center py-6"><Spinner className="text-primary w-6 h-6" /></div>}
              {expert.bio && (<div><p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">{t("hr.network.about")}</p><p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{expert.bio}</p></div>)}
              {(expert.fields || []).length > 0 && (<div><p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">{t("hr.network.focusAreas")}</p><div className="flex flex-wrap gap-1.5">{expert.fields.map((f) => <Pill key={f} className="bg-primary/10 text-primary"><Target size={11} className="mr-1" />{f}</Pill>)}</div></div>)}
              {(expert.expertise || []).length > 0 && (<div><p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">{t("hr.network.expertise")}</p><div className="flex flex-wrap gap-1.5">{expert.expertise.map((x) => <Pill key={x} className="bg-secondary text-foreground">{x}</Pill>)}</div></div>)}
              {(expert.certifications || []).length > 0 && (<div><p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">{t("hr.network.credentials")}</p><div className="space-y-1.5">{expert.certifications.map((c) => <div key={c} className="flex items-center gap-2 text-sm text-foreground"><Award size={14} className="text-primary" /> {c}</div>)}</div></div>)}
              {(expert.reviews || []).length > 0 && (<div><p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">{t("hr.network.reviews")}</p><div className="space-y-2">{expert.reviews.slice(0, 4).map((r, idx) => (<div key={`${r.name || "rev"}-${idx}`} className="rounded-lg border border-border p-3"><div className="flex items-center gap-2"><span className="inline-flex items-center gap-1 text-xs text-warning"><Star size={12} className="fill-warning" /> {r.rating}.0</span><span className="text-xs text-muted-foreground">· {r.name}</span></div>{r.comment && <p className="text-sm text-foreground mt-1.5 leading-relaxed">{r.comment}</p>}</div>))}</div></div>)}
            </div>
            <div className="sticky bottom-0 border-t border-border bg-card p-4">
              {status === "in_network" ? (
                <Button data-testid="sheet-remove-network" onClick={() => onRemove(expert.expert_id)} variant="outline" className="w-full rounded-lg gap-1.5"><X size={15} /> {t("hr.network.removeFromNetwork")}</Button>
              ) : status === "outgoing" ? (
                <Button disabled variant="outline" className="w-full rounded-lg gap-1.5 text-muted-foreground"><Clock size={15} /> {t("hr.network.invitationSent")}</Button>
              ) : status === "incoming" ? (
                <div className="text-center text-sm text-muted-foreground">{t("hr.network.askedToJoin")}</div>
              ) : (
                <Button data-testid="sheet-invite-network" onClick={() => onInvite(expert.expert_id)} className="w-full rounded-lg gap-1.5"><Plus size={15} /> {t("hr.network.inviteToNetwork")}</Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* -------------------- Requests tab -------------------- */
export function RequestRow({ r, kind, onAccept, onDecline, onWithdraw, onView }) {
  const { t } = useTranslation();
  const e = r.expert || {};
  return (
    <div data-testid={`req-${kind}-${r.request_id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-border p-4 hover:bg-secondary/30 transition-colors">
      <button type="button" onClick={() => onView(e)} className="flex items-center gap-3 min-w-0 flex-1 text-left group">
        <UserAvatar src={fileUrl(e.picture)} name={e.name} size={44} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5"><p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{e.name}</p>{e.verified && <VerifiedBadge label="" />}</div>
          <p className="text-xs text-muted-foreground truncate">{e.title}</p>
          {r.message && <p className="text-xs text-muted-foreground italic mt-1 line-clamp-1">“{r.message}”</p>}
        </div>
      </button>
      <div className="flex items-center gap-2 shrink-0">
        {kind === "incoming" ? (
          <>
            <Button data-testid={`decline-req-${r.request_id}`} size="sm" variant="outline" onClick={() => onDecline(r.request_id)} className="rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5"><X size={14} /> {t("common.decline")}</Button>
            <Button data-testid={`accept-req-${r.request_id}`} size="sm" onClick={() => onAccept(r.request_id)} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><Check size={14} /> {t("common.accept")}</Button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-warning"><Clock size={13} /> {t("common.pending")}</span>
            <Button data-testid={`withdraw-req-${r.request_id}`} size="sm" variant="outline" onClick={() => onWithdraw(r.request_id)} className="rounded-lg gap-1.5">{t("common.withdraw")}</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function RequestsView({ requests, onAccept, onDecline, onWithdraw, onView, onBrowse }) {
  const { t } = useTranslation();
  const incoming = requests.incoming || [];
  const outgoing = requests.outgoing || [];
  if (incoming.length === 0 && outgoing.length === 0) {
    return <EmptyState icon={Send} title={t("hr.network.reqEmptyTitle")} description={t("hr.network.reqEmptyDesc")}
      action={<Button className="rounded-lg gap-1.5" onClick={onBrowse}><Sparkles size={15} /> {t("hr.network.discoverExperts")}</Button>} />;
  }
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <section className="card-milled rounded-[0.8rem] p-6" data-testid="requests-incoming">
        <div className="flex items-center gap-2 mb-4"><span className="grid place-items-center w-8 h-8 rounded-lg bg-secondary/80 text-primary"><Inbox size={16} /></span>
          <div><h3 className="text-sm font-semibold text-foreground">{t("hr.network.incomingTitle")}</h3><p className="text-xs text-muted-foreground">{t("hr.network.incomingDesc")}</p></div>
          {incoming.length > 0 && <span className="ml-auto min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-warning/15 text-warning text-[11px] font-semibold">{incoming.length}</span>}
        </div>
        {incoming.length ? <div className="space-y-3">{incoming.map((r) => <RequestRow key={r.request_id} r={r} kind="incoming" onAccept={onAccept} onDecline={onDecline} onView={onView} />)}</div>
          : <p className="text-sm text-muted-foreground py-6 text-center">{t("hr.network.noIncoming")}</p>}
      </section>
      <section className="card-milled rounded-[0.8rem] p-6" data-testid="requests-outgoing">
        <div className="flex items-center gap-2 mb-4"><span className="grid place-items-center w-8 h-8 rounded-lg bg-secondary/80 text-primary"><Send size={16} /></span>
          <div><h3 className="text-sm font-semibold text-foreground">{t("hr.network.outgoingTitle")}</h3><p className="text-xs text-muted-foreground">{t("hr.network.outgoingDesc")}</p></div>
          {outgoing.length > 0 && <span className="ml-auto min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold">{outgoing.length}</span>}
        </div>
        {outgoing.length ? <div className="space-y-3">{outgoing.map((r) => <RequestRow key={r.request_id} r={r} kind="outgoing" onWithdraw={onWithdraw} onView={onView} />)}</div>
          : <p className="text-sm text-muted-foreground py-6 text-center">{t("hr.network.noOutgoing")}</p>}
      </section>
    </div>
  );
}
